import { ProductImageOrVideoViewState } from "@faire/retailer-visitor-shared/views/product/ProductImages/useProductImageOrVideoViewState";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { useObservable } from "@faire/web--source/ui/hooks/useObservable";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// percentage of user session that will trigger image viewing event
const SAMPLE_RATE = 1;
const SHOULD_TRACK = Math.random() * 100 < SAMPLE_RATE;
const PRODUCT_IMAGE_GALLERY_TOKEN = "productImageGallery";

export const useImageGalleryHandler = () => {
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const closedByBackButton = useRef(false);

  const isLocationStateGalleryOpened =
    location.state === PRODUCT_IMAGE_GALLERY_TOKEN;

  const openImageGallery = useCallback(() => {
    if (!isImageGalleryOpen) {
      history.push({ ...location, state: PRODUCT_IMAGE_GALLERY_TOKEN });
    }
    setIsImageGalleryOpen(true);
    closedByBackButton.current = false; // reset the flag when we open the gallery
  }, [isImageGalleryOpen, history, location]);

  const closeImageGallery = useCallback(() => {
    if (isImageGalleryOpen && !closedByBackButton.current) {
      history.goBack();
    }
    setIsImageGalleryOpen(false);
    closedByBackButton.current = false; // reset the flag when we close the gallery
  }, [closedByBackButton, history, isImageGalleryOpen]);

  // listen to browser back button event to close the modal in this case:
  useEffect(() => {
    const handlePopState = () => {
      // Indicate that the gallery is being closed by the back button
      closedByBackButton.current = true;
      closeImageGallery();
    };

    getWindow()?.addEventListener("popstate", handlePopState);

    return () => {
      getWindow()?.removeEventListener("popstate", handlePopState);
    };
  }, [closeImageGallery]);

  return {
    openImageGallery,
    closeImageGallery,
    isImageGalleryOpen: isImageGalleryOpen || isLocationStateGalleryOpened,
  };
};

export const useTrackImageChangeProductEvent = (
  state: ProductImageOrVideoViewState,
  token?: string
) => {
  const imageChangeProductEventPrefix = state.selectedImage
    ? `product/${token}/image/${state.selectedImage.token}`
    : null;

  const selectedImage = useObservable(() => state.selectedImage);
  const images = useObservable(() => state.images);

  const hasSentTrackEventDefaultRef = useRef(false);
  useEffect(() => {
    if (
      images.length > 0 &&
      selectedImage &&
      !hasSentTrackEventDefaultRef.current &&
      SHOULD_TRACK &&
      imageChangeProductEventPrefix
    ) {
      trackEvent(`${imageChangeProductEventPrefix}/default`);
      hasSentTrackEventDefaultRef.current = true;
    }
  }, [imageChangeProductEventPrefix, images.length, selectedImage]);

  useEffect(() => {
    if (SHOULD_TRACK) {
      if (imageChangeProductEventPrefix) {
        trackEvent(`${imageChangeProductEventPrefix}/change`);
      }
    }
  }, [imageChangeProductEventPrefix]);
};
